import React, { useEffect } from 'react';
import { Stack, Copy } from '@grubhubprod/gh-cookbook-react';
import { ConfirmationStepCardContainer } from 'src/components/selfService/features/Signup/steps/ConfirmationStep/ConfirmationStepCardContainer/ConfirmationStepCardContainer';
import {
  SignupStep,
  useSignupStatus,
} from 'src/components/selfService/features/Signup/context/SignupStatusProvider/SignupStatusProvider';
import { useFetchBalancePaymentMethodsCards } from 'src/components/selfService/common/Balance/hooks/useFetchBalancePaymentMethodsCards';
import { CreditCardDisplay } from 'src/components/selfService/common/Balance/components/CreditCardDisplay/CreditCardDisplay';
import { ConfirmationSummaryRow } from 'src/components/selfService/features/Signup/steps/ConfirmationStep/ConfirmationSummaryRow/ConfirmationSummaryRow';
import {
  getCreditLimitText,
  getDisplayedCreditCard,
  getPaymentMethodDescription,
} from './ValidationSummaryCard.utils';

interface ValidationSummaryCardProps {
  onDataLoaded: () => void;
}

export function ValidationSummaryCard({
  onDataLoaded,
}: ValidationSummaryCardProps): JSX.Element {
  const { balance, getStepNumber } = useSignupStatus();
  const {
    isLoading: isLoadingPaymentMethods,
    hasError: hasPaymentMethodsError,
    balancePaymentMethodsCards,
    fetchBalancePaymentMethodsCards,
  } = useFetchBalancePaymentMethodsCards(balance.externalId);

  const creditCard = getDisplayedCreditCard(balancePaymentMethodsCards);

  useEffect(() => {
    const isDataLoaded = !isLoadingPaymentMethods && !hasPaymentMethodsError;
    if (isDataLoaded) {
      onDataLoaded();
    }
  }, [isLoadingPaymentMethods, hasPaymentMethodsError, onDataLoaded]);

  return (
    <ConfirmationStepCardContainer
      stepName="Financial information"
      isLoadingData={isLoadingPaymentMethods}
      hasError={hasPaymentMethodsError}
      reloadData={fetchBalancePaymentMethodsCards}
      linkText="Edit"
      stepNumber={getStepNumber(SignupStep.VALIDATION)}
    >
      <ConfirmationSummaryRow label="Status" y="x-sm">
        Approved
      </ConfirmationSummaryRow>

      <ConfirmationSummaryRow label="Credit limit" y="x-sm">
        {getCreditLimitText(balance.creditLimit)}
      </ConfirmationSummaryRow>

      {creditCard && (
        <ConfirmationSummaryRow label="Payment" y="none">
          <Stack y="x-sm">
            <CreditCardDisplay
              brand={creditCard.brand}
              expiration={{
                month: creditCard.expirationMonth,
                year: creditCard.expirationYear,
              }}
              lastFourDigits={creditCard.lastFourDigits}
              isDefault={false}
              showBorder={false}
            />
          </Stack>
          <Copy variant="caption" color="secondaryText">
            {getPaymentMethodDescription(balance.creditLimit)}
          </Copy>
        </ConfirmationSummaryRow>
      )}
    </ConfirmationStepCardContainer>
  );
}
